import { useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { Button } from 'components/Button';
import { Divider } from 'components/Divider';
import { EmptyState } from 'components/EmptyState';
import { InfoIcon, WarningCircleIcon } from 'components/Icon';
import { Search } from 'components/Search';
import { Skeleton } from 'components/Skeleton';

import {
  filterCountRecipe,
  filterFooterRecipe,
  filterListRecipe,
  filterPanelRecipe,
  filterSkeletonRowRecipe,
  filterWarningRecipe,
} from './FilterButton.recipe';
import { FilterOptionRow } from './FilterOptionRow';

import type { FilterApplyMode, FilterButtonLabels, FilterOption, FilterOptionsStatus, FilterValidation } from './types';

/** Rows drawn while loading: one per row the panel is likely to show, so it does not jump on arrival. */
const SKELETON_ROWS = 6;

type FilterPanelProps = {
  mode: 'multiple' | 'single';
  applyMode: FilterApplyMode;
  labels: FilterButtonLabels;
  options: FilterOption[];
  status: FilterOptionsStatus;
  onRetry?: () => void;
  searchable: boolean;
  /** The draft in `apply` mode, the applied value in `live` mode. Always a list; a single pick is one. */
  selected: string[];
  onSelectedChange: (next: string[]) => void;
  /** The guard's verdict on the draft. `apply` mode only. */
  validation?: FilterValidation;
  onClear: () => void;
  onApply: () => void;
  children?: ReactNode;
};

/**
 * The panel's content: Figma's `Filter panel`, minus the surface, which is `Popover`'s.
 *
 * It holds no selection of its own. The button hands it `selected` and takes the next list back, so
 * the draft-versus-live decision is made in one place (`FilterButton`) and this stays a picture of a
 * list. The search text is the exception, since nothing outside the panel needs it, and it resets
 * because `PopoverContent` unmounts when the panel closes.
 *
 * In `live` mode there is no Apply and the guard does not run: a pick has already happened by the
 * time a guard could refuse it, so a guarded filter is an `apply` filter.
 */
export const FilterPanel = ({
  mode,
  applyMode,
  labels,
  options,
  status,
  onRetry,
  searchable,
  selected,
  onSelectedChange,
  validation,
  onClear,
  onApply,
  children,
}: FilterPanelProps) => {
  const groupName = useId();
  const [query, setQuery] = useState('');
  const multiple = mode === 'multiple';
  const custom = children !== undefined;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? options.filter((option) => option.label.toLowerCase().includes(needle)) : options;
  }, [options, query]);

  const selectable = options.filter((option) => !option.disabled);
  const allSelected = selectable.length > 0 && selectable.every((option) => selected.includes(option.value));
  const someSelected = selected.length > 0 && !allSelected;

  const toggle = (value: string) => {
    if (!multiple) return onSelectedChange([value]);
    onSelectedChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };
  const toggleAll = () => onSelectedChange(allSelected ? [] : selectable.map((option) => option.value));

  const showSearch = searchable && !custom && status !== 'error';
  const ready = status === 'ready';
  const invalid = applyMode === 'apply' && validation?.valid === false;
  const message = applyMode === 'apply' ? validation?.message : undefined;

  const renderBody = () => {
    if (custom) return children;

    if (status === 'loading') {
      return (
        <div aria-busy="true" className={filterListRecipe()} data-part="list">
          <span className="sr-only" role="status">
            {labels.loading}
          </span>
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <div key={index} className={filterSkeletonRowRecipe()}>
              {multiple && <Skeleton className="size-[18px] shrink-0" shape="block" />}
              <Skeleton className="w-full" />
            </div>
          ))}
        </div>
      );
    }

    if (status === 'error') {
      return (
        <EmptyState
          actions={
            <Button appearance="secondary" size="large" onClick={onRetry}>
              {labels.retry}
            </Button>
          }
          description={labels.loadErrorDescription}
          headingLevel={3}
          icon={WarningCircleIcon}
          size="inline"
          title={labels.loadError}
        />
      );
    }

    if (visible.length === 0) return <EmptyState headingLevel={3} size="inline" title={labels.noOptions} />;

    return (
      <div
        aria-label={labels.panel}
        className={filterListRecipe()}
        data-part="list"
        role={multiple ? 'group' : 'radiogroup'}
      >
        {multiple && !query.trim() && (
          <>
            <FilterOptionRow
              checked={allSelected}
              indeterminate={someSelected}
              label={labels.selectAll}
              mode="multiple"
              name={groupName}
              onChange={toggleAll}
            />
            <Divider />
          </>
        )}
        {visible.map((option) => (
          <FilterOptionRow
            key={option.value}
            checked={selected.includes(option.value)}
            disabled={option.disabled}
            label={option.label}
            mode={mode}
            name={groupName}
            onChange={() => toggle(option.value)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={filterPanelRecipe()} data-part="filter-panel">
      {showSearch && (
        <Search
          clearLabel={labels.clearSearch}
          label={labels.search}
          placement="menu"
          size="small"
          value={query}
          onValueChange={setQuery}
        />
      )}

      {renderBody()}

      {/* Always mounted, so a screen reader is already listening when the message arrives. */}
      <div className={filterWarningRecipe()} role="status">
        {message && (
          <>
            <InfoIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <span>{message}</span>
          </>
        )}
      </div>

      <Divider />

      <div className={filterFooterRecipe()} data-part="footer">
        {multiple && ready && !custom && (
          <span aria-live="polite" className={filterCountRecipe()} data-part="count">
            {labels.selected(selected.length)}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button appearance="secondary" disabled={selected.length === 0} size="xs" onClick={onClear}>
            {labels.clearPanel}
          </Button>
          {applyMode === 'apply' && (
            <Button appearance="primary" disabled={invalid || !ready} size="xs" onClick={onApply}>
              {labels.apply}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
